import mustache from "mustache";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import { cargarTablaPorCultivoAsync } from "../../../../../modulos/catalogos/monitoreo/services/MonitoreoCultivoPlagaGrpcCatalogService";
import { monitoreoPlagaEnfermedadGrpcCatalogService } from "../../../../../modulos/catalogos/monitoreo/services/MonitoreoPlagaEnfermedadGrpcCatalogService";
import {
  leerResultado,
  SesionDashboardItem,
  SesionDashboardLote,
} from "../../../../../modulos/monitoreo/sesion/response/SesionDashboardResponse";
import {
  agruparPorSemana,
  formatRangoSemana,
  SemanaMonitoreo,
} from "../../../../../modulos/monitoreo/sesion/agruparPorSemana";
import { fx_createSesionDashboardGrpcService } from "../../../../../modulos/monitoreo/sesion/services/SesionDashboardGrpcService";
import {
  evaluarEnLote,
  indexPlagasById,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { MonitoreoAnalisisChartRender } from "./MonitoreoAnalisisChartRender";
import {
  LOTE_MAX_SERIES,
  siguienteColorLibre,
} from "./monitoreoAnalisisPaleta";
import {
  computeTendencias,
  renderTendenciasHtml,
} from "./monitoreoAnalisisTendencias";
import template from "./monitoreoAnalisisTemplate.html";
import { formatPorcentaje } from "../../../../../modulos/monitoreo/sesion/formatIncidencia";

mustache.parse(template);

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let CONTENT_ID = 0;

/**
 * Cuántas plagas se activan al entrar: **una**, la de mayor presencia de
 * alerta.
 *
 * No es solo por cantidad de curvas (con varias plagas y varios lotes se
 * llegaba a 24 encimadas). Es que las **bandas de umbral solo se dibujan con
 * una plaga activa** —con varias, cada una tiene su propio umbral y
 * superponerlos confunde—, así que arrancar con tres dejaba el gráfico sin la
 * referencia contra la cual hay que leer las curvas.
 *
 * Con una sola, la pregunta que responde el gráfico de entrada es la correcta:
 * qué lotes cruzaron el umbral de esta plaga. Comparar lotes contra UN umbral
 * es una lectura; comparar tres umbrales distintos a la vez, no.
 */
const PLAGAS_ACTIVAS_POR_DEFECTO = 1;

/**
 * Cuántas semanas se muestran al entrar, contando desde la más reciente.
 *
 * Un año entero de monitoreo semanal son hasta 52 puntos en el eje X, y el
 * gráfico de entrada se vuelve ilegible por el lado del tiempo. Un trimestre es
 * lo que se mira casi siempre —cómo viene la presión últimamente, no qué pasó
 * en marzo— y los selectores siguen ahí para ampliar. Si el año tiene menos
 * semanas con datos, se muestran todas.
 */
const SEMANAS_POR_DEFECTO = 12;


/** Cuerpo del dashboard histórico de monitoreo de plagas
 *  (`/monitoreo/analisis`).
 *
 *  Carga el histórico de la finca activa junto con el catálogo de plagas, y
 *  desde ahí arma las dos piezas que comparten una única selección: el gráfico
 *  de curvas y la franja de tendencias.
 *
 *  **No genera el reporte imprimible**: eso vive en su propia pantalla
 *  (`/monitoreo/plagas`), y acá solo queda el botón "Ver informes" que lleva
 *  hasta allá. Antes había un selector de sesión con su botón "Exportar", pero
 *  duplicaba esa pantalla y —al listar solo las sesiones del período elegido—
 *  acoplaba de forma invisible el filtro del gráfico con la elección del
 *  informe.
 *
 *  **El período se elige por semanas, no por fechas**: año + "de la semana A a
 *  la semana B". Es el mismo lenguaje que hablan el eje del gráfico, el badge y
 *  el informe, y como los selectores solo ofrecen semanas **con datos**, es
 *  imposible elegir un tramo vacío — con campos de fecha sí se podía caer entre
 *  dos recorridos y dejar el gráfico en blanco. Tampoco hay ya dos controles
 *  compitiendo por el mismo eje: el año fija el universo y las semanas recortan
 *  dentro.
 *
 *  Todo el filtrado es **en memoria**, sin re-consultar con `?anio=`: no hay
 *  forma de saber qué años tienen datos sin traerlos, así que la carga inicial
 *  es completa de todos modos. El parámetro ya está soportado en el service
 *  para cuando el server pueda informar los años disponibles.
 *
 *  La franja climática del mockup quedó **fuera del alcance** por decisión de
 *  producto; no confundir con el módulo de clima de la app
 *  (`ClimaCurrentPage`/`ClimaForecastPage`), que es independiente. */
export class MonitoreoAnalisisContentRender implements IConnectRender {
  /**
   * @param _onBack        Acción del botón volver.
   * @param _onVerInformes Lleva a la pantalla de informes de sesión. Entra por
   *                       callback y no como ruta importada: este componente
   *                       vive en `shared/` y no puede depender de las
   *                       constantes de ruta de `cliente/`.
   */
  constructor(
    private readonly _onBack: () => void,
    private readonly _onVerInformes: () => void,
  ) {
    CONTENT_ID += 1;
    this._id = CONTENT_ID;
  }

  render(): string {
    return mustache.render(template, {
      id: this._id,
      nombreFinca: serviceProvider.getFincaSelectedService().getRequired()
        .nombreFinca,
    });
  }

  connect(): void {
    this._backBtn = document.getElementById(`monBack-${this._id}`);
    this._anioSelect = document.getElementById(
      `monAnio-${this._id}`,
    ) as HTMLSelectElement | null;
    this._desdeSelect = document.getElementById(
      `monDesde-${this._id}`,
    ) as HTMLSelectElement | null;
    this._hastaSelect = document.getElementById(
      `monHasta-${this._id}`,
    ) as HTMLSelectElement | null;
    this._badgeEl = document.getElementById(`monBadge-${this._id}`);
    this._informesBtn = document.getElementById(`monInformes-${this._id}`);
    this._controlsEl = document.getElementById(`monControls-${this._id}`);
    this._lotePillsEl = document.getElementById(`monLotePills-${this._id}`);
    this._plagaPillsEl = document.getElementById(`monPlagaPills-${this._id}`);
    this._filtrosEl = document.getElementById(`monFiltros-${this._id}`);
    this._bodyEl = document.getElementById(`monBody-${this._id}`);
    this._chartEl = document.getElementById(`monChart-${this._id}`);
    this._subtitleEl = document.getElementById(`monSubtitle-${this._id}`);
    this._umbralesEl = document.getElementById(`monUmbrales-${this._id}`);
    this._trendsEl = document.getElementById(`monTrends-${this._id}`);
    this._stateEl = document.getElementById(`monState-${this._id}`);

    this._backBtn?.addEventListener("click", this._onBack);
    this._anioSelect?.addEventListener("change", this._onAnioChange);
    this._desdeSelect?.addEventListener("change", this._onSemanaChange);
    this._hastaSelect?.addEventListener("change", this._onSemanaChange);
    this._informesBtn?.addEventListener("click", this._onVerInformes);
    this._lotePillsEl?.addEventListener("click", this._onLotePillClick);
    this._plagaPillsEl?.addEventListener("click", this._onPlagaPillClick);
    this._filtrosEl?.addEventListener("click", this._onFiltroClick);

    void this._loadAsync();
  }

  dispose(): void {
    this._disposed = true;
    this._backBtn?.removeEventListener("click", this._onBack);
    this._anioSelect?.removeEventListener("change", this._onAnioChange);
    this._desdeSelect?.removeEventListener("change", this._onSemanaChange);
    this._hastaSelect?.removeEventListener("change", this._onSemanaChange);
    this._informesBtn?.removeEventListener("click", this._onVerInformes);
    this._lotePillsEl?.removeEventListener("click", this._onLotePillClick);
    this._plagaPillsEl?.removeEventListener("click", this._onPlagaPillClick);
    this._filtrosEl?.removeEventListener("click", this._onFiltroClick);
    this._chart?.dispose();
    this._chart = null;
  }

  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number;

  private _backBtn: HTMLElement | null = null;
  private _anioSelect: HTMLSelectElement | null = null;
  private _desdeSelect: HTMLSelectElement | null = null;
  private _hastaSelect: HTMLSelectElement | null = null;
  private _badgeEl: HTMLElement | null = null;
  private _informesBtn: HTMLElement | null = null;
  private _controlsEl: HTMLElement | null = null;
  private _lotePillsEl: HTMLElement | null = null;
  private _plagaPillsEl: HTMLElement | null = null;
  private _filtrosEl: HTMLElement | null = null;
  private _bodyEl: HTMLElement | null = null;
  private _chartEl: HTMLElement | null = null;
  private _subtitleEl: HTMLElement | null = null;
  private _umbralesEl: HTMLElement | null = null;
  private _trendsEl: HTMLElement | null = null;
  private _stateEl: HTMLElement | null = null;

  /** Si el componente ya fue dispuesto (guarda para la carga async). */
  private _disposed = false;

  /** Histórico completo, ascendente por fecha. */
  private _todasSesiones: SesionDashboardItem[] = [];
  /** Sesiones dentro del rango elegido. */
  private _sesiones: SesionDashboardItem[] = [];
  /** Las mismas sesiones agrupadas por semana ISO: es la unidad del eje de
   *  tiempo y lo que consumen el gráfico y las tendencias. Un lote se recorre
   *  una vez por semana (o cada quince días), así que varias sesiones de la
   *  misma semana son el mismo punto. */
  private _semanas: SemanaMonitoreo[] = [];
  /** Todas las semanas del año elegido, ascendente. Es el universo del filtro:
   *  los dos selectores recortan un tramo de acá. */
  private _semanasAnio: SemanaMonitoreo[] = [];
  /** Posición de la primera semana del tramo activo dentro de `_semanasAnio`. */
  private _indiceDesde = 0;
  /** Posición de la última semana del tramo activo. */
  private _indiceHasta = 0;
  /** Catálogo de lotes que manda el server en la raíz. */
  private _lotesCatalogo: SesionDashboardLote[] = [];
  /** Nombre por `loteId`, para mostrar. */
  private _loteNombres = new Map<string, string>();
  /** Color por `loteId`, **solo de los lotes activos**. El slot se toma al
   *  activar y se libera al desactivar, así cualquier lote de la finca puede
   *  graficarse aunque haya más lotes que colores.
   *
   *  Se **muta**, nunca se reasigna: el gráfico guarda esta misma referencia y
   *  la lee en cada repintado. Cambiarla por un Map nuevo dejaría al gráfico
   *  leyendo el viejo hasta que se lo recree. */
  private readonly _loteColores = new Map<string, string>();
  /** Catálogo de plagas indexado por id. */
  private _plagasById = new Map<string, MonitoreoPlagaEnfermedadItem>();
  /** Plagas con alguna lectura mayor a cero en el año elegido: el catálogo
   *  completo trae plagas de otros cultivos que acá serían ruido. */
  private _plagasConDatos: MonitoreoPlagaEnfermedadItem[] = [];
  /** Ids de lote monitoreados en el año elegido, ordenados por nombre. */
  private _lotes: string[] = [];

  /** Ids de lote encendidos. */
  private _activeLotes: string[] = [];
  /** Ids de plaga encendidos. */
  private _activePlagas: string[] = [];

  /** Gráfico vivo (se recrea al cambiar de año: cambia el set de sesiones). */
  private _chart: MonitoreoAnalisisChartRender | null = null;

  /** Carga histórico + catálogo y arma la vista, o deja un mensaje de estado. */
  private async _loadAsync(): Promise<void> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    try {
      const [dashboard, plagas] = await Promise.all([
        fx_createSesionDashboardGrpcService(finca.id).getAsync(),
        monitoreoPlagaEnfermedadGrpcCatalogService.getListAsync(),
        cargarTablaPorCultivoAsync(),
      ]);
      if (this._disposed) return;

      this._todasSesiones = dashboard.sesiones;
      this._lotesCatalogo = dashboard.lotes;
      this._plagasById = indexPlagasById(plagas);
      this._plagasCatalogo = plagas;

      if (this._todasSesiones.length === 0) {
        this._setState("Esta finca todavía no tiene sesiones de monitoreo.");
        return;
      }

      this._loteNombres = new Map(
        this._lotesCatalogo.map((lote) => [lote.id, lote.nombre]),
      );

      this._renderAnioSelect();
      this._setState("");
      if (this._controlsEl) this._controlsEl.style.display = "";
      if (this._bodyEl) this._bodyEl.style.display = "";
      if (this._informesBtn) this._informesBtn.style.display = "";

      // Arranca en el año más reciente con datos (el caso habitual), no en
      // todo el historial: un eje X con años de sesiones semanales es
      // ilegible de entrada.
      this._aplicarAnio(this._getAniosDisponibles()[0]);
    } catch {
      if (this._disposed) return;
      this._setState("No se pudo cargar el histórico de monitoreo.");
    }
  }

  /** Catálogo completo de plagas (para recalcular al cambiar de año). */
  private _plagasCatalogo: MonitoreoPlagaEnfermedadItem[] = [];

  /** Años con sesiones, del más reciente al más viejo. */
  private _getAniosDisponibles(): number[] {
    const anios = new Set(
      this._todasSesiones.map((sesion) => Number(sesion.fecha.slice(0, 4))),
    );
    return [...anios].sort((a, b) => b - a);
  }

  /** Puebla el selector de año con los años que tienen datos. El año define el
   *  universo del filtro; las semanas recortan dentro. */
  private _renderAnioSelect(): void {
    if (!this._anioSelect) return;
    this._anioSelect.innerHTML = this._getAniosDisponibles()
      .map((anio) => `<option value="${anio}">${anio}</option>`)
      .join("");
  }

  /** Cambia de año: arma sus semanas y arranca mostrándolas todas. */
  private _aplicarAnio(anio: number): void {
    const sesionesDelAnio = this._todasSesiones.filter(
      (sesion) => Number(sesion.fecha.slice(0, 4)) === anio,
    );
    this._semanasAnio = agruparPorSemana(sesionesDelAnio);
    this._indiceHasta = Math.max(this._semanasAnio.length - 1, 0);
    this._indiceDesde = Math.max(
      0,
      this._semanasAnio.length - SEMANAS_POR_DEFECTO,
    );

    this._renderSemanaSelects();
    this._aplicarRango();
  }

  /** Puebla los dos selectores con las semanas del año. Solo se ofrecen semanas
   *  **con datos**: así es imposible elegir un tramo vacío, que es lo que sí
   *  permitían los campos de fecha (dos días entre recorridos dejaban el
   *  gráfico en blanco). */
  private _renderSemanaSelects(): void {
    const opciones = this._semanasAnio
      .map(
        (semana, indice) =>
          `<option value="${indice}">Semana ${semana.semana} · ${formatRangoSemana(semana)}</option>`,
      )
      .join("");

    if (this._desdeSelect) {
      this._desdeSelect.innerHTML = opciones;
      this._desdeSelect.value = String(this._indiceDesde);
    }
    if (this._hastaSelect) {
      this._hastaSelect.innerHTML = opciones;
      this._hastaSelect.value = String(this._indiceHasta);
    }
  }

  /** Acota el histórico al tramo de semanas elegido y rearma todo lo que
   *  depende de él. */
  private _aplicarRango(): void {
    this._semanas = this._semanasAnio.slice(
      this._indiceDesde,
      this._indiceHasta + 1,
    );
    // Los recorridos del tramo, para el badge: puede haber más de uno por
    // semana.
    this._sesiones = this._semanas.flatMap((semana) => semana.sesiones);

    this._lotes = this._collectLotes();
    this._plagasConDatos = this._collectPlagasConDatos(this._plagasCatalogo);

    // La selección se recalcula por tramo: un lote o una plaga pueden no
    // existir en el que se acaba de elegir.
    this._seleccionarLotesPorDefecto();
    this._activePlagas = this._getPlagasPorDefecto();

    this._renderBadge();
    this._renderFiltros();
    this._renderPills();
    this._mountChart();
    this._applySelection();
  }

  /** (Re)crea el gráfico para el set de sesiones actual. Se recrea en vez de
   *  refrescarse porque cambia el eje X completo, no solo las series. */
  private _mountChart(): void {
    if (!this._chartEl) return;
    this._chart?.dispose();
    this._chart = new MonitoreoAnalisisChartRender(
      this._semanas,
      this._plagasById,
      this._loteColores,
      this._loteNombres,
    );
    this._chartEl.innerHTML = this._chart.render();
    this._chart.connect();
  }

  /** Resumen del rango cubierto por el año elegido. */
  private _renderBadge(): void {
    if (!this._badgeEl) return;
    if (this._sesiones.length === 0) {
      this._badgeEl.textContent = "Sin sesiones en el rango elegido";
      return;
    }
    const numeros = this._semanas.map((semana) => semana.semana);
    const desde = Math.min(...numeros);
    const hasta = Math.max(...numeros);
    const rango =
      desde === hasta ? `Semana ${desde}` : `Semanas ${desde}–${hasta}`;

    // El gráfico tiene un punto por semana, pero puede haber más recorridos que
    // semanas. Se nombran los dos números cuando difieren: si no, el badge diría
    // "14 sesiones" sobre un eje con 4 puntos y parecería que faltan datos.
    const recorridos = this._sesiones.length;
    const semanas = this._semanas.length;
    const detalle =
      recorridos === semanas
        ? `${semanas} ${semanas === 1 ? "semana" : "semanas"}`
        : `${semanas} ${semanas === 1 ? "semana" : "semanas"} · ${recorridos} recorridos`;

    this._badgeEl.textContent = `${rango} · ${detalle}`;
  }

  /** Ids de lote monitoreados en el año elegido (presentes en `datos` de alguna
   *  sesión), ordenados por nombre. */
  private _collectLotes(): string[] {
    const lotes = new Set<string>();
    for (const semana of this._semanas) {
      for (const loteId of Object.keys(semana.datos)) lotes.add(loteId);
    }
    return [...lotes].sort((a, b) =>
      (this._loteNombres.get(a) ?? a).localeCompare(
        this._loteNombres.get(b) ?? b,
      ),
    );
  }

  /** Plagas del catálogo con al menos una lectura mayor a cero en el año
   *  elegido, en el orden del catálogo. */
  private _collectPlagasConDatos(
    plagas: MonitoreoPlagaEnfermedadItem[],
  ): MonitoreoPlagaEnfermedadItem[] {
    const conDatos = new Set<string>();
    for (const semana of this._semanas) {
      for (const lote of Object.values(semana.datos)) {
        for (const [plagaId, valor] of Object.entries(lote.inc)) {
          if (valor > 0) conDatos.add(plagaId);
        }
      }
    }
    return plagas
      .filter((plaga) => conDatos.has(plaga.id))
      .sort((a, b) => a.orden - b.orden);
  }

  /** Plagas encendidas al abrir: las que más veces cruzaron el umbral de
   *  alerta — que es lo que el agrónomo quiere ver primero. Si ninguna llegó a
   *  alerta, las primeras del catálogo. */
  private _getPlagasPorDefecto(): string[] {
    const alertas = this._plagasConDatos
      .map((plaga) => ({ id: plaga.id, conteo: this._contarAlertas(plaga) }))
      .filter((entrada) => entrada.conteo > 0)
      .sort((a, b) => b.conteo - a.conteo)
      .slice(0, PLAGAS_ACTIVAS_POR_DEFECTO)
      .map((entrada) => entrada.id);

    if (alertas.length > 0) return alertas;
    return this._plagasConDatos
      .slice(0, PLAGAS_ACTIVAS_POR_DEFECTO)
      .map((plaga) => plaga.id);
  }

  /** Cuántas combinaciones sesión × lote llegaron a alerta para esa plaga. */
  private _contarAlertas(plaga: MonitoreoPlagaEnfermedadItem): number {
    let conteo = 0;
    for (const semana of this._semanas) {
      for (const loteId of Object.keys(semana.datos)) {
        const lote = leerResultado(semana, loteId);
        if (!lote) continue;
        if (evaluarEnLote(lote, plaga.id, plaga)?.estado === "alert") conteo += 1;
      }
    }
    return conteo;
  }

  /** Filtros rápidos: uno por grupo real del catálogo (`grupo` es texto libre
   *  del server — "Ácaros", "Insecto"…— así que se derivan de los datos en vez
   *  de codificarlos a mano), más "Solo alertas" y "Restablecer". */
  private _renderFiltros(): void {
    if (!this._filtrosEl) return;
    const grupos = [
      ...new Set(
        this._plagasConDatos.map((plaga) => plaga.grupo).filter((g) => !!g),
      ),
    ].sort((a, b) => a.localeCompare(b));

    this._filtrosEl.innerHTML = [
      `<button class="sx-btn sx-btn-outline sx-btn-sm" data-filtro="alertas">Solo alertas</button>`,
      ...grupos.map(
        (grupo) =>
          `<button class="sx-btn sx-btn-outline sx-btn-sm" data-filtro="grupo" data-grupo="${grupo}">${grupo}</button>`,
      ),
      `<button class="sx-btn sx-btn-ghost sx-btn-sm" data-filtro="reset">Restablecer</button>`,
    ].join("");
  }


  /** Repinta ambas filas de pills según la selección actual. */
  private _renderPills(): void {
    if (this._lotePillsEl) {
      this._lotePillsEl.innerHTML = this._lotes
        .map((loteId) => this._renderLotePill(loteId))
        .join("");
    }
    if (this._plagaPillsEl) {
      this._plagaPillsEl.innerHTML = this._plagasConDatos
        .map((plaga) => this._renderPlagaPill(plaga))
        .join("");
    }
  }

  /** Pill de lote: punto con su color fijo + nombre. Es además el "alivio" de
   *  contraste de la paleta — mantiene visible el mapeo color → lote en texto,
   *  para que la identidad de la curva nunca dependa solo del tono. */
  private _renderLotePill(loteId: string): string {
    const nombre = this._loteNombres.get(loteId) ?? loteId;
    const color = this._loteColores.get(loteId);
    // Sin color = no está activo. Ya no hay lotes inalcanzables: cualquiera se
    // puede activar liberando un slot.
    if (!color) {
      return `<button class="mon-pill" data-lote="${loteId}">
        <span class="mon-pill-dot"></span>${nombre}</button>`;
    }
    return `<button class="mon-pill mon-pill--active" data-lote="${loteId}"
      style="background:${color};border-color:${color}">
      <span class="mon-pill-dot" style="background:rgba(255,255,255,.75)"></span>${nombre}</button>`;
  }

  /** Pill de plaga. El estado activo va en gris oscuro y no en el color de la
   *  plaga: el color ya codifica el lote, y reusarlo acá rompería esa lectura. */
  private _renderPlagaPill(plaga: MonitoreoPlagaEnfermedadItem): string {
    const activa = this._activePlagas.includes(plaga.id);
    const umbrales = this._textoUmbrales(plaga);
    return `<button class="mon-pill${activa ? " mon-pill--active mon-pill--plaga" : ""}"
      data-plaga="${plaga.id}" title="${plaga.nombreComun} — ${umbrales}">
      <span class="mon-pill-dot"></span>${plaga.nombreComun}</button>`;
  }

  /** Propaga la selección al gráfico, al subtítulo y a las tendencias. */
  private _applySelection(): void {
    this._chart?.setSelection(this._activeLotes, this._activePlagas);

    const plagaUnica =
      this._activePlagas.length === 1
        ? this._plagasById.get(this._activePlagas[0])
        : undefined;

    if (this._subtitleEl) {
      // El aviso de la sección 11.3 va en el subtítulo: si alguna semana del
      // rango se evaluó con otra versión de las referencias, dos puntos del
      // mismo color pueden venir de umbrales distintos y nada más en pantalla
      // lo delata.
      const aviso = this._chart?.getAvisoVersiones();
      const base = plagaUnica
        ? `${plagaUnica.nombreComun} · ${plagaUnica.nombreTecnico} — ${this._textoUmbrales(plagaUnica)}`
        : `${this._activePlagas.length} plagas · ${this._activeLotes.length} lotes`;
      this._subtitleEl.textContent = aviso ? `${base} — ${aviso}` : base;
    }

    // La leyenda de umbrales solo tiene sentido cuando el gráfico dibuja las
    // bandas y sus líneas, o sea con una sola plaga activa. Una plaga crítica
    // no tiene gradiente, así que tampoco las tiene.
    if (this._umbralesEl) {
      const conUmbrales =
        !!plagaUnica &&
        !plagaUnica.isCritico &&
        this._getUmbralesIncidencia(plagaUnica.id) !== null;
      this._umbralesEl.style.display = conUmbrales ? "" : "none";
    }

    if (this._trendsEl) {
      this._trendsEl.innerHTML = renderTendenciasHtml(
        computeTendencias(
          this._semanas,
          this._plagasById,
          this._activeLotes,
          this._activePlagas,
          this._loteNombres,
        ),
      );
    }
  }

  /** Mensaje de estado (cargando / vacío / error); vacío lo oculta. */
  private _setState(texto: string): void {
    if (!this._stateEl) return;
    this._stateEl.textContent = texto;
    this._stateEl.style.display = texto ? "" : "none";
  }

  /** Activa un lote tomando el próximo color libre. `false` si la paleta está
   *  llena: el gráfico no puede distinguir más series sin repetir tonos. */
  private _activarLote(loteId: string): boolean {
    const color = siguienteColorLibre(this._loteColores.values());
    if (!color) return false;
    this._loteColores.set(loteId, color);
    this._activeLotes = [...this._activeLotes, loteId];
    return true;
  }

  /** Desactiva un lote y devuelve su color al pool. */
  private _desactivarLote(loteId: string): void {
    this._loteColores.delete(loteId);
    this._activeLotes = this._activeLotes.filter((actual) => actual !== loteId);
  }

  /** Selección inicial de lotes: los que más veces cruzaron alerta en el tramo,
   *  hasta llenar la paleta. Antes se tomaban los primeros alfabéticamente, así
   *  que en una finca con más lotes que colores se mostraban ocho arbitrarios
   *  en vez de los ocho con problema. El nombre queda como desempate, que es
   *  también el orden cuando no hay ninguna alerta. */
  private _seleccionarLotesPorDefecto(): void {
    this._loteColores.clear();
    this._activeLotes = [];

    const porSeveridad = [...this._lotes].sort((a, b) => {
      const diferencia = this._contarAlertasLote(b) - this._contarAlertasLote(a);
      if (diferencia !== 0) return diferencia;
      return (this._loteNombres.get(a) ?? a).localeCompare(
        this._loteNombres.get(b) ?? b,
      );
    });

    for (const loteId of porSeveridad) {
      if (!this._activarLote(loteId)) break;
    }
  }

  /** Cuántas combinaciones semana × plaga llegaron a alerta en ese lote. */
  private _contarAlertasLote(loteId: string): number {
    let conteo = 0;
    for (const semana of this._semanas) {
      const lote = leerResultado(semana, loteId);
      if (!lote) continue;
      for (const plaga of this._plagasConDatos) {
        if (evaluarEnLote(lote, plaga.id, plaga)?.estado === "alert") conteo += 1;
      }
    }
    return conteo;
  }

  /**
   * Umbrales de incidencia de un organismo, tomados de la referencia más
   * reciente del rango.
   *
   * No se leen `rangoBajo`/`rangoMedio` del catálogo: son umbrales de **nivel**
   * (individuos o ácaros por lupada) y acá se muestran al lado de un gráfico de
   * **incidencia** (% de árboles). Mostrar "alerta: 0,2" junto a una serie que
   * llega a 46 era comparar dos escalas distintas.
   *
   * Cuando exista `listMonitoreoCultivoPlagas` (6.2) esto lee los valores
   * vigentes de la tabla por cultivo, que es lo que la sección 11.3 pide para
   * las franjas.
   */
  private _getUmbralesIncidencia(
    plagaId: string,
  ): { bajo: number; medio: number } | null {
    for (let i = this._semanas.length - 1; i >= 0; i -= 1) {
      for (const loteId of Object.keys(this._semanas[i].datos)) {
        const referencia = leerResultado(this._semanas[i], loteId)?.referencias[
          plagaId
        ];
        const bajo = referencia?.incidenciaBajoMax;
        const medio = referencia?.incidenciaMedioMax;
        if (bajo !== undefined && medio !== undefined && medio > 0) {
          return { bajo, medio };
        }
      }
    }
    return null;
  }

  /** Texto de los umbrales de un organismo, para el título de su pill y el
   *  subtítulo del gráfico. */
  private _textoUmbrales(plaga: MonitoreoPlagaEnfermedadItem): string {
    if (plaga.isCritico) {
      return "Organismo crítico: cualquier detección es alerta";
    }
    const umbrales = this._getUmbralesIncidencia(plaga.id);
    if (!umbrales) return "Sin valores de referencia";
    return `Árboles afectados — precaución: ${formatPorcentaje(umbrales.bajo)} · alerta: ${formatPorcentaje(umbrales.medio)}`;
  }

  /** Alterna un valor dentro de una selección. */
  private _toggle(seleccion: string[], valor: string): string[] {
    return seleccion.includes(valor)
      ? seleccion.filter((actual) => actual !== valor)
      : [...seleccion, valor];
  }

  private _onAnioChange = (): void => {
    if (!this._anioSelect) return;
    this._aplicarAnio(Number(this._anioSelect.value));
  };

  private _onSemanaChange = (event: Event): void => {
    const desde = Number(this._desdeSelect?.value ?? 0);
    const hasta = Number(this._hastaSelect?.value ?? 0);
    this._indiceDesde = desde;
    this._indiceHasta = hasta;

    // Rango invertido: se arrastra el OTRO extremo hasta el que se acaba de
    // tocar, en vez de rechazar el cambio. Así el control siempre obedece lo
    // que el usuario eligió recién.
    if (desde > hasta) {
      if (event.target === this._desdeSelect) this._indiceHasta = desde;
      else this._indiceDesde = hasta;
    }

    this._renderSemanaSelects();
    this._aplicarRango();
  };

  private _onLotePillClick = (event: Event): void => {
    const pill = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-lote]",
    );
    const loteId = pill?.dataset.lote;
    if (!loteId) return;

    if (this._loteColores.has(loteId)) {
      this._desactivarLote(loteId);
    } else if (!this._activarLote(loteId)) {
      // Paleta llena: no se toca nada y se explica por qué, en vez de que el
      // clic no haga aparentemente nada.
      serviceProvider
        .getErrorService()
        .addWarning(
          "Comparación de lotes",
          `El gráfico distingue hasta ${LOTE_MAX_SERIES} lotes a la vez. Desactive uno para agregar este.`,
        );
      return;
    }

    this._renderPills();
    this._applySelection();
  };

  private _onPlagaPillClick = (event: Event): void => {
    const pill = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-plaga]",
    );
    if (!pill?.dataset.plaga) return;
    this._activePlagas = this._toggle(this._activePlagas, pill.dataset.plaga);
    this._renderPills();
    this._applySelection();
  };

  private _onFiltroClick = (event: Event): void => {
    const boton = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-filtro]",
    );
    if (!boton) return;

    // Todos los filtros rehacen la selección de lotes por severidad: acotan
    // por plaga, pero sin dejar activo un lote que ya no venga al caso.
    this._seleccionarLotesPorDefecto();

    if (boton.dataset.filtro === "alertas") {
      const enAlerta = this._plagasConDatos
        .filter((plaga) => this._contarAlertas(plaga) > 0)
        .map((plaga) => plaga.id);
      this._activePlagas =
        enAlerta.length > 0 ? enAlerta.slice(0, 4) : this._getPlagasPorDefecto();
    } else if (boton.dataset.filtro === "grupo") {
      this._activePlagas = this._plagasConDatos
        .filter((plaga) => plaga.grupo === boton.dataset.grupo)
        .map((plaga) => plaga.id);
    } else {
      this._activePlagas = this._getPlagasPorDefecto();
    }

    this._renderPills();
    this._applySelection();
  };

}
